import React, { useEffect, useState } from "react";
import { Formik, Form, Field, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import {
  Avatar,
  Button,
  CircularProgress,
  Collapse,
  TextField,
  Select,
  MenuItem,
  Chip,
  Typography,
  Grid,
  Paper,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Switch,
  FormControlLabel,
  Divider,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import GetAppIcon from "@material-ui/icons/GetApp";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Action, ActionType, Subaction } from "../../../../../types/ActionType";
import { Author } from "../../../../../types/Author";
import { Community } from "../../../../../types/Community";
import { Habit } from "../../../../../types/Habit";

import ImageUpload from "../../../../common/ImageUpload";
import VideoUpload from "../../../../common/VideoUpload";
import DB_PATH from "../../../../../database/DB_PATH";
import { firestore } from "../../../../../database/firebase";
import { Plan } from "../../../../../types/Plan";
import { normalizeAiGuidelines } from "../../../../../utils/aiGuidelines";
import ActionFormButton from "../../../../common/ActionFormButton";
import { generateRandomId } from "../../../../../database/hooks/generateRandomId";
import {
  generateReflectionPromptPack,
  generatePromptStarters,
} from "../../../onboarding/OnboardingAPI";
type ActionFormProps = {
  onToggleFullScreenDialog: () => void;
  onCreateItem: (action: Action) => void;
  onEditItem: (action: Action) => void;
  item: Action | null;
  habitId: string;
  mode: "create" | "edit";
  communityId: string;
};
const actionTypes: ActionType[] = [
  ActionType.EXERCISE,
  ActionType.DIET,
  ActionType.MEDITATION,
  ActionType.SLEEP,
  ActionType.CUSTOM,
];

// Subactions support one extra type the parent action does not: a chat-only
// "prompt" step. Only the subaction Action Type dropdown uses this list.
const subactionTypes: ActionType[] = [...actionTypes, ActionType.PROMPT];

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  addButton: {
    marginTop: theme.spacing(2),
  },
  subactionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  learningPlanSelect: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  subactionsWorkspace: {
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  subactionsToolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  subactionsToolbarContent: {
    maxWidth: 560,
  },
  subactionsToolbarActions: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
    "& > *": {
      marginLeft: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  subactionCountChip: {
    marginLeft: theme.spacing(1),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  subactionPanel: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    overflow: "hidden",
    "&:before": {
      display: "none",
    },
  },
  subactionPanelSummaryContent: {
    margin: `${theme.spacing(1)}px 0 !important`,
  },
  subactionPanelDetails: {
    display: "block",
    paddingTop: 0,
  },
  subactionSummaryRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  subactionAvatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    marginRight: theme.spacing(2),
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
    fontWeight: 700,
  },
  subactionSummaryText: {
    flex: 1,
    minWidth: 0,
  },
  subactionSummaryTitleRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  subactionSummaryDescription: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  subactionSummaryChips: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
    "& > *": {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  subactionSection: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  subactionSectionHeader: {
    marginBottom: theme.spacing(2),
  },
  subactionSectionTitle: {
    fontWeight: 600,
  },
  subactionSectionDescription: {
    color: theme.palette.text.secondary,
  },
  uploadHint: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  ingredientRow: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  subactionDeleteRow: {
    marginTop: theme.spacing(2),
  },
}));
const measurementSizes = [
  "g",
  "kg",
  "mg",
  "oz",
  "lb",
  "ml",
  "l",
  "fl oz",
  "cup",
  "tsp",
  "tbsp",
  "pinch",
  "slice",
  "piece",
  "whole",
];

const isYouTubeUrl = (url: string) => {
  return url?.includes("youtube.com") || url?.includes("youtu.be");
};

const formatLabel = (value?: string) => {
  if (!value) return "";
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const truncateText = (value?: string, maxLength = 120) => {
  if (!value) return "";
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1).trim()}...`;
};

const buildSubactionSummary = (subaction: Subaction) => {
  const summary: string[] = [formatLabel(subaction.actionType || ActionType.CUSTOM)];

  if (subaction.duration) summary.push(`${subaction.duration}s`);
  if (subaction.actionType === ActionType.EXERCISE && subaction.repetitions) {
    summary.push(`${subaction.repetitions} reps`);
  }
  if (subaction.actionType === ActionType.EXERCISE && subaction.sets) {
    summary.push(`${subaction.sets} sets`);
  }
  if (subaction.actionType === ActionType.DIET && subaction.ingredients?.length) {
    summary.push(`${subaction.ingredients.length} ingredients`);
  }
  if (subaction.backgroundImageURL) summary.push("Image");
  if (subaction.videoURL) {
    summary.push(isYouTubeUrl(subaction.videoURL) ? "YouTube video" : "Uploaded video");
  }
  if (subaction.tags?.length) summary.push(`${subaction.tags.length} tags`);

  return summary;
};

const createEmptySubaction = (): Subaction => ({
  id: generateRandomId(),
  name: "",
  description: "",
  actionType: ActionType.CUSTOM,
  backgroundImageURL: "",
  videoURL: "",
  createdAt: new Date().toISOString(),
});

const getSubactionGenerationKey = (subaction: Subaction, index: number) =>
  subaction.id?.trim() || `subaction-${index}`;

// Build the shared Community/Author/Habit/Action/Subaction context object passed
// to the AI prompt-pack generators. Reused by both the reflection-prompt and the
// conversation-starters generators so they read from identical signal.
const buildPromptPackContext = ({
  community,
  author,
  action,
  habit,
  subaction,
}: {
  community: Community;
  author: Author | null;
  action: Action;
  habit: Habit;
  subaction: Subaction;
}) => ({
  community: {
    title: community.title,
    description: community.description,
    shortDescription: community.shortDescription,
    categoryName: community.categoryName,
    tags: community.tags || [],
    writingStyle: community.writingStyle || "",
    visualStyle: community.visualStyle || "",
  },
  author: {
    fullName: author?.fullName || community.author?.fullName || "",
    description: author?.description || community.author?.description || "",
    shortDescription: author?.shortDescription || "",
    aiGuidelines:
      normalizeAiGuidelines(author?.aiGuidelines) ||
      normalizeAiGuidelines(community.author?.aiGuidelines),
    tags: author?.tags || [],
  },
  habit: {
    habitName: habit.habitName,
    habitDescription: habit.habitDescription,
    targetFrequency: habit.targetFrequency,
  },
  action: {
    name: action.name,
    description: action.description,
    actionType: action.actionType,
    duration: action.duration,
    goal: action.goal,
    tags: Array.isArray(action.tags) ? action.tags : [],
  },
  subaction: {
    name: subaction.name,
    description: subaction.description,
    actionType: subaction.actionType || action.actionType,
    duration: subaction.duration,
    intensityLevel: subaction.intensityLevel,
    notes: subaction.notes,
    primaryIntent: subaction.primaryIntent,
    secondaryBenefits: subaction.secondaryBenefits,
    bodyRegions: subaction.bodyRegions || [],
    movementQualities: subaction.movementQualities || [],
    userUseCases: subaction.userUseCases || [],
    tags: Array.isArray(subaction.tags) ? subaction.tags : [],
  },
});

const SubactionSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => {
  const classes = useStyles();

  return (
    <Grid item xs={12}>
      <Box className={classes.subactionSection}>
        <Box className={classes.subactionSectionHeader}>
          <Typography variant="subtitle1" className={classes.subactionSectionTitle}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" className={classes.subactionSectionDescription}>
              {description}
            </Typography>
          )}
        </Box>
        <Grid container spacing={2}>
          {children}
        </Grid>
      </Box>
    </Grid>
  );
};

// Editable list of plain strings (one row each, with add/remove). Used for the
// prompt subaction's intro messages and template answers.
const StringListField = ({
  name,
  addLabel,
  placeholder,
  multiline = false,
}: {
  name: string;
  addLabel: string;
  placeholder?: string;
  multiline?: boolean;
}) => (
  <FieldArray name={name}>
    {(arrayHelpers) => {
      const items: string[] = getIn(arrayHelpers.form.values, name) || [];

      return (
        <React.Fragment>
          {items.map((_, itemIndex) => (
            <Grid item xs={12} key={itemIndex}>
              <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                <Field
                  name={`${name}.${itemIndex}`}
                  as={TextField}
                  fullWidth
                  placeholder={placeholder}
                  multiline={multiline}
                  rows={multiline ? 2 : undefined}
                />
                <IconButton
                  onClick={() => arrayHelpers.remove(itemIndex)}
                  color="secondary"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => arrayHelpers.push("")}
            >
              {addLabel}
            </Button>
          </Grid>
        </React.Fragment>
      );
    }}
  </FieldArray>
);

export const SubactionForm = ({
  index,
  remove = () => {},
  subaction,
  setFieldValue,
  showRemove = true,
  onGenerateReflectionPrompt,
  isGeneratingReflectionPrompt = false,
  reflectionPromptError = "",
  isPromptContextLoading = false,
  onEnhancePrompts,
  isEnhancingPrompts = false,
  enhanceError = "",
}: {
  index;
  remove?: (index: number) => void;
  subaction: Subaction;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  showRemove?: boolean;
  onGenerateReflectionPrompt?: (index: number) => void;
  isGeneratingReflectionPrompt?: boolean;
  reflectionPromptError?: string;
  isPromptContextLoading?: boolean;
  onEnhancePrompts?: (index: number) => void;
  isEnhancingPrompts?: boolean;
  enhanceError?: string;
}) => {
  const classes = useStyles();
  // A "prompt" subaction is chat-only: it drops the workout/diet execution and
  // classification fields and keeps just the overview plus the existing chat
  // prompt (in Media & Guidance) as its content. We also treat any subaction
  // that already carries a reflectionPrompt as a prompt subaction so data
  // created before this type existed stays backward compatible.
  const isPromptSubaction =
    subaction.actionType === ActionType.PROMPT ||
    Boolean(subaction.reflectionPrompt);
  const [useUploadedVideo, setUseUploadedVideo] = useState(
    subaction.videoURL ? !isYouTubeUrl(subaction.videoURL) : false
  );
  const [isExpanded, setIsExpanded] = useState(
    !showRemove || !subaction.name || !subaction.description
  );
  // Start with advanced fields collapsed for a clean form, but auto-expand when
  // the subaction already has any advanced data so nothing populated is hidden.
  const hasAdvancedData = Boolean(
    subaction.videoURL ||
      subaction.instructionsURL ||
      subaction.backgroundImageURL ||
      subaction.reflectionPrompt ||
      subaction.notes ||
      subaction.caloriesBurned ||
      subaction.intensityLevel ||
      subaction.quantity ||
      subaction.location ||
      (subaction.equipmentNeeded && subaction.equipmentNeeded.length) ||
      (subaction.tags && subaction.tags.length) ||
      subaction.videoFileName ||
      subaction.phase ||
      subaction.difficultyLevel ||
      subaction.loadType ||
      subaction.primaryIntent ||
      subaction.secondaryBenefits ||
      (subaction.bodyRegions && subaction.bodyRegions.length) ||
      (subaction.movementQualities && subaction.movementQualities.length) ||
      (subaction.compensationsAddressed &&
        subaction.compensationsAddressed.length) ||
      (subaction.userUseCases && subaction.userUseCases.length)
  );
  const [showAdvanced, setShowAdvanced] = useState(hasAdvancedData);

  // Reveal the advanced fields whenever a chat prompt gets populated (e.g. after
  // "Enhance prompts") so the generated prompt is immediately visible. Runs only
  // when the prompt value changes, so a manual collapse afterwards is respected.
  useEffect(() => {
    if (subaction.reflectionPrompt) {
      setShowAdvanced(true);
    }
  }, [subaction.reflectionPrompt]);

  const listToString = (value: any) =>
    Array.isArray(value) ? value.join(", ") : value || "";
  const stringToList = (value: string) =>
    value
      .split(/[|,;]+/)
      .map((item) => item.trim())
      .filter(Boolean);

  useEffect(() => {
    if (!subaction.id) {
      const newId = generateRandomId();
      setFieldValue(`subactions.${index}.id`, newId);
    }
  }, [index, setFieldValue, subaction.id]);

  const summaryChips = buildSubactionSummary(subaction);
  const summaryTitle = subaction.name || `Subaction ${index + 1}`;
  const summaryDescription =
    truncateText(subaction.description, 140) ||
    "Add the core details, then use the grouped sections below for media, guidance, and metadata.";

  return (
    <ExpansionPanel
      expanded={isExpanded}
      onChange={(_, expanded) => setIsExpanded(expanded)}
      className={classes.subactionPanel}
    >
      <ExpansionPanelSummary
        expandIcon={<ExpandMoreIcon />}
        classes={{ content: classes.subactionPanelSummaryContent }}
      >
        <Box className={classes.subactionSummaryRow}>
          <Avatar
            variant="rounded"
            src={subaction.backgroundImageURL}
            className={classes.subactionAvatar}
          >
            {index + 1}
          </Avatar>
          <Box className={classes.subactionSummaryText}>
            <Box className={classes.subactionSummaryTitleRow}>
              <Typography variant="subtitle1">{summaryTitle}</Typography>
              <Chip
                size="small"
                label={`#${index + 1}`}
                className={classes.subactionCountChip}
                variant="outlined"
              />
            </Box>
            <Typography variant="body2" className={classes.subactionSummaryDescription}>
              {summaryDescription}
            </Typography>
            <Box className={classes.subactionSummaryChips}>
              {summaryChips.map((summaryItem) => (
                <Chip
                  key={`${summaryTitle}-${summaryItem}`}
                  size="small"
                  label={summaryItem}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </Box>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className={classes.subactionPanelDetails}>
        <Grid container spacing={3}>
          <SubactionSection
            title="Overview"
            description="Start with the core information you want visible when scanning subactions."
          >
            <Grid item xs={12} sm={6}>
              <Field
                name={`subactions.${index}.name`}
                as={TextField}
                label="Name"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field name={`subactions.${index}.actionType`}>
                {({ field }) => (
                  <TextField {...field} select label="Action Type" fullWidth>
                    {subactionTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {formatLabel(type)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field
                name={`subactions.${index}.description`}
                as={TextField}
                label="Description"
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </SubactionSection>

          {isPromptSubaction && (
            <SubactionSection
              title="Conversation starters"
              description="Author the opening bubbles and tappable replies shown when this prompt chat opens."
            >
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  style={{ gap: 12 }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Auto-generate the chat prompt, intro messages, and template
                    answers from the current Community, Author, Habit, Action, and
                    Subaction context.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => onEnhancePrompts?.(index)}
                    disabled={
                      isEnhancingPrompts ||
                      isPromptContextLoading ||
                      !onEnhancePrompts
                    }
                    startIcon={
                      isEnhancingPrompts ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : undefined
                    }
                  >
                    {isEnhancingPrompts
                      ? "Enhancing..."
                      : isPromptContextLoading
                      ? "Loading Context..."
                      : "Enhance prompts"}
                  </Button>
                </Box>
                {enhanceError ? (
                  <Typography variant="caption" color="error">
                    {enhanceError}
                  </Typography>
                ) : null}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2">Introduction messages</Typography>
                <Typography variant="caption" color="textSecondary">
                  Shown instantly as the first chat bubbles, before the assistant
                  responds. Leave empty to let the assistant open the conversation
                  itself.
                </Typography>
              </Grid>
              <StringListField
                name={`subactions.${index}.introMessages`}
                addLabel="Add intro message"
                placeholder="Hey, glad you're here. What's on your mind today?"
                multiline
              />

              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2">Template answers</Typography>
                <Typography variant="caption" color="textSecondary">
                  Tappable starter replies shown under the intro so the user can
                  begin with one tap.
                </Typography>
              </Grid>
              <StringListField
                name={`subactions.${index}.suggestedReplies`}
                addLabel="Add template answer"
                placeholder="I want to talk through a decision"
              />
            </SubactionSection>
          )}

          {!isPromptSubaction && (
          <SubactionSection
            title="Essentials"
            description="The key execution details for this subaction."
          >
            <Grid item xs={12} sm={4}>
              <Field
                name={`subactions.${index}.duration`}
                as={TextField}
                label="Duration (seconds)"
                type="number"
                fullWidth
              />
            </Grid>
            {subaction.actionType === ActionType.EXERCISE && (
              <Grid item xs={12} sm={4}>
                <Field
                  name={`subactions.${index}.repetitions`}
                  as={TextField}
                  label="Repetitions"
                  type="number"
                  fullWidth
                />
              </Grid>
            )}
            {subaction.actionType === ActionType.EXERCISE && (
              <Grid item xs={12} sm={4}>
                <Field
                  name={`subactions.${index}.sets`}
                  as={TextField}
                  label="Sets"
                  type="number"
                  fullWidth
                />
              </Grid>
            )}
            {subaction.actionType === ActionType.EXERCISE && (
              <Grid item xs={12} sm={4}>
                <Field
                  name={`subactions.${index}.restTime`}
                  as={TextField}
                  label="Rest Time (seconds)"
                  type="number"
                  fullWidth
                />
              </Grid>
            )}
          </SubactionSection>
          )}

          {subaction.actionType === ActionType.DIET && (
            <SubactionSection
              title="Ingredients"
              description="Break diet subactions into structured ingredients for easier editing."
            >
              <FieldArray name={`subactions.${index}.ingredients`}>
                {(arrayHelpers) => {
                  const ingredients =
                    arrayHelpers.form.values.subactions[index].ingredients || [];

                  return (
                    <React.Fragment>
                      {ingredients.map((ingredient, ingredientIndex) => (
                        <Grid item xs={12} key={ingredientIndex}>
                          <Box className={classes.ingredientRow}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={4}>
                                <Field
                                  name={`subactions.${index}.ingredients.${ingredientIndex}.name`}
                                  as={TextField}
                                  label="Ingredient Name"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <Field
                                  name={`subactions.${index}.ingredients.${ingredientIndex}.quantity`}
                                  as={TextField}
                                  label="Quantity"
                                  type="number"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <Field
                                  name={`subactions.${index}.ingredients.${ingredientIndex}.measurementSize`}
                                >
                                  {({ field }) => (
                                    <TextField {...field} select label="Measurement Size" fullWidth>
                                      {measurementSizes.map((size) => (
                                        <MenuItem key={size} value={size}>
                                          {size}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  )}
                                </Field>
                              </Grid>
                              <Grid item xs={12} sm={1}>
                                <Box display="flex" justifyContent="flex-end">
                                  <IconButton
                                    onClick={() => arrayHelpers.remove(ingredientIndex)}
                                    color="secondary"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                      ))}
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() =>
                            arrayHelpers.push({
                              name: "",
                              quantity: 0,
                              measurementSize: "",
                            })
                          }
                        >
                          Add Ingredient
                        </Button>
                      </Grid>
                    </React.Fragment>
                  );
                }}
              </FieldArray>
            </SubactionSection>
          )}

          <Grid item xs={12}>
            <Button
              color="primary"
              onClick={() => setShowAdvanced((prev) => !prev)}
              startIcon={
                <ExpandMoreIcon
                  style={{
                    transform: showAdvanced ? "rotate(180deg)" : "none",
                    transition: "transform 150ms ease",
                  }}
                />
              }
            >
              {showAdvanced ? "Hide advanced fields" : "Show advanced fields"}
            </Button>
          </Grid>

          <Grid item xs={12} style={{ paddingTop: 0 }}>
            <Collapse in={showAdvanced}>
              <Grid container spacing={3}>
                {!isPromptSubaction && (
                <SubactionSection
                  title="More details"
                  description="Secondary effort, classification, and tagging fields."
                >
                  <Grid item xs={12} sm={4}>
                    <Field
                      name={`subactions.${index}.caloriesBurned`}
                      as={TextField}
                      label="Calories Burned"
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field
                      name={`subactions.${index}.intensityLevel`}
                      as={TextField}
                      label="Intensity Level"
                      type="text"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field
                      name={`subactions.${index}.quantity`}
                      as={TextField}
                      label="Quantity"
                      type="text"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field
                      name={`subactions.${index}.location`}
                      as={TextField}
                      label="Location"
                      type="text"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Equipment Needed"
                      type="text"
                      fullWidth
                      value={listToString(subaction.equipmentNeeded)}
                      onChange={(event) =>
                        setFieldValue(
                          `subactions.${index}.equipmentNeeded`,
                          stringToList(event.target.value)
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Tags"
                      type="text"
                      fullWidth
                      value={listToString(subaction.tags)}
                      onChange={(event) =>
                        setFieldValue(
                          `subactions.${index}.tags`,
                          stringToList(event.target.value)
                        )
                      }
                    />
                  </Grid>
                </SubactionSection>
                )}

                <SubactionSection
                  title="Media & Guidance"
                  description="Attach the media and supporting prompts people need to complete the step."
                >
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useUploadedVideo}
                          onChange={(event) => {
                            setUseUploadedVideo(event.target.checked);
                            setFieldValue(`subactions.${index}.videoURL`, "");
                          }}
                          color="primary"
                        />
                      }
                      label="Upload Video"
                    />
                    {useUploadedVideo ? (
                      <Field name={`subactions.${index}.videoURL`}>
                        {({ field, form }) => (
                          <VideoUpload
                            field={field}
                            form={{ setFieldValue: form.setFieldValue }}
                            subactionId={subaction.id}
                            reference="subaction-videos"
                          />
                        )}
                      </Field>
                    ) : (
                      <Field
                        name={`subactions.${index}.videoURL`}
                        as={TextField}
                        label="YouTube Video URL"
                        type="text"
                        fullWidth
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Field
                      name={`subactions.${index}.instructionsURL`}
                      as={TextField}
                      label="Instructions URL"
                      type="text"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name={`subactions.${index}.backgroundImageURL`}
                      as={TextField}
                      label="Background Image URL"
                      type="text"
                      fullWidth
                    />
                    <Typography variant="body2" className={classes.uploadHint}>
                      Paste a URL or upload an image below. Uploading replaces the current image URL.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field name={`subactions.${index}.backgroundImageURL`}>
                      {({ field, form }) => (
                        <ImageUpload
                          field={field}
                          form={{ setFieldValue: form.setFieldValue }}
                          actionId={subaction.id || `subaction-${index}`}
                          reference="subaction-images"
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      mb={1}
                      style={{ gap: 12 }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        Generate the system prompt used by the assistant chat inside this subaction
                        from the current Community, Author, Habit, Action, and Subaction context.
                      </Typography>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => onGenerateReflectionPrompt?.(index)}
                        disabled={
                          isGeneratingReflectionPrompt ||
                          isPromptContextLoading ||
                          !onGenerateReflectionPrompt
                        }
                        startIcon={
                          isGeneratingReflectionPrompt ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : undefined
                        }
                      >
                        {isGeneratingReflectionPrompt
                          ? "Generating..."
                          : isPromptContextLoading
                          ? "Loading Context..."
                          : "Generate Prompt"}
                      </Button>
                    </Box>
                    <Field
                      name={`subactions.${index}.reflectionPrompt`}
                      as={TextField}
                      label="Subaction Chat Prompt"
                      helperText='This is the system prompt for the assistant chat inside this subaction. Start with "You are ..." in the author voice when available, and make the first message warm and assumption-free.'
                      placeholder="You are Herb Lipsman, the practical and experienced voice guiding this subaction chat. Help the user explore what feels unclear, what they want to understand, and what would be most useful to unpack right now. Keep responses warm, concise, and practical. Ask one short question at a time when more context is needed."
                      fullWidth
                      multiline
                      rows={3}
                    />
                    {reflectionPromptError ? (
                      <Typography variant="caption" color="error">
                        {reflectionPromptError}
                      </Typography>
                    ) : null}
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name={`subactions.${index}.notes`}
                      as={TextField}
                      label="Notes"
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Grid>
                </SubactionSection>

                {!isPromptSubaction && (
                <SubactionSection
                  title="Library Metadata"
                  description="Optional metadata for search, classification, and the content library."
                >
                  <Grid item xs={12} sm={4}>
                    <Field
                      name={`subactions.${index}.videoFileName`}
                      as={TextField}
                      label="Video File Name"
                      type="text"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field
                      name={`subactions.${index}.phase`}
                      as={TextField}
                      label="Phase"
                      type="text"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field
                      name={`subactions.${index}.difficultyLevel`}
                      as={TextField}
                      label="Difficulty Level"
                      type="text"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field
                      name={`subactions.${index}.loadType`}
                      as={TextField}
                      label="Load Type"
                      type="text"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name={`subactions.${index}.primaryIntent`}
                      as={TextField}
                      label="Primary Intent"
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name={`subactions.${index}.secondaryBenefits`}
                      as={TextField}
                      label="Secondary Benefits"
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Body Regions"
                      type="text"
                      fullWidth
                      value={listToString(subaction.bodyRegions)}
                      onChange={(event) =>
                        setFieldValue(
                          `subactions.${index}.bodyRegions`,
                          stringToList(event.target.value)
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Movement Qualities"
                      type="text"
                      fullWidth
                      value={listToString(subaction.movementQualities)}
                      onChange={(event) =>
                        setFieldValue(
                          `subactions.${index}.movementQualities`,
                          stringToList(event.target.value)
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Compensations Addressed"
                      type="text"
                      fullWidth
                      value={listToString(subaction.compensationsAddressed)}
                      onChange={(event) =>
                        setFieldValue(
                          `subactions.${index}.compensationsAddressed`,
                          stringToList(event.target.value)
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="User Use Cases"
                      type="text"
                      fullWidth
                      value={listToString(subaction.userUseCases)}
                      onChange={(event) =>
                        setFieldValue(
                          `subactions.${index}.userUseCases`,
                          stringToList(event.target.value)
                        )
                      }
                    />
                  </Grid>
                </SubactionSection>
                )}
              </Grid>
            </Collapse>
          </Grid>

          {showRemove && (
            <Grid item xs={12}>
              <Divider />
              <Box display="flex" justifyContent="flex-end" className={classes.subactionDeleteRow}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<DeleteIcon />}
                  onClick={() => remove(index)}
                >
                  Remove Subaction
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
};

const ActionForm: React.FC<ActionFormProps> = (props) => {
  const classes = useStyles();
  const {
    onToggleFullScreenDialog,
    onCreateItem,
    onEditItem,
    item,
    habitId,
    mode,
    communityId,
  } = props;
  const [learningPlans, setLearningPlans] = useState<Plan[]>([]);
  const [communityContext, setCommunityContext] = useState<Community | null>(null);
  const [habitContext, setHabitContext] = useState<Habit | null>(null);
  const [authorContext, setAuthorContext] = useState<Author | null>(null);
  const [isLoadingPromptContext, setIsLoadingPromptContext] = useState(false);
  const [promptContextError, setPromptContextError] = useState("");
  const [promptGenerationByKey, setPromptGenerationByKey] = useState<
    Record<string, boolean>
  >({});
  const [promptErrorByKey, setPromptErrorByKey] = useState<Record<string, string>>(
    {}
  );
  const [enhanceGenerationByKey, setEnhanceGenerationByKey] = useState<
    Record<string, boolean>
  >({});
  const [enhanceErrorByKey, setEnhanceErrorByKey] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    let isMounted = true;

    const fetchActionFormContext = async () => {
      setIsLoadingPromptContext(true);
      setPromptContextError("");

      try {
        const [plansSnapshot, communityDoc, habitDoc] = await Promise.all([
          firestore
            .collection(DB_PATH.PLAN)
            .where("communityId", "==", communityId)
            .get(),
          firestore.collection(DB_PATH.COMMUNITY).doc(communityId).get(),
          firestore.collection(DB_PATH.HABIT).doc(habitId).get(),
        ]);

        if (!isMounted) return;

        const plansData = plansSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        //@ts-ignore
        setLearningPlans(plansData);

        const nextCommunity = communityDoc.exists
          ? ({ id: communityDoc.id, ...communityDoc.data() } as Community)
          : null;
        const nextHabit = habitDoc.exists
          ? ({ id: habitDoc.id, ...habitDoc.data() } as Habit)
          : null;

        setCommunityContext(nextCommunity);
        setHabitContext(nextHabit);

        let nextAuthor: Author | null = null;

        if (nextCommunity?.author?.id?.trim()) {
          const authorDoc = await firestore
            .collection(DB_PATH.AUTHOR)
            .doc(nextCommunity.author.id.trim())
            .get();

          if (authorDoc.exists) {
            nextAuthor = {
              id: authorDoc.id,
              ...authorDoc.data(),
            } as Author;
          }
        }

        if (!nextAuthor) {
          const authorSnapshot = await firestore
            .collection(DB_PATH.AUTHOR)
            .where("communityId", "==", communityId)
            .limit(1)
            .get();

          if (!authorSnapshot.empty) {
            const authorDoc = authorSnapshot.docs[0];
            nextAuthor = {
              id: authorDoc.id,
              ...authorDoc.data(),
            } as Author;
          }
        }

        if (!nextAuthor && nextCommunity?.author) {
          nextAuthor = {
            id: nextCommunity.author.id || "",
            fullName: nextCommunity.author.fullName || "",
            profilePhotoURL: nextCommunity.author.profilePhotoURL || "",
            coverURL: "",
            description: nextCommunity.author.description || "",
            shortDescription: "",
            tags: [],
            createdAt: Date.now(),
            communityId,
            voiceId: nextCommunity.author.voiceId || "",
            agentId: nextCommunity.author.agentId || "",
            aiGuidelines: normalizeAiGuidelines(
              nextCommunity.author.aiGuidelines
            ),
          };
        }

        if (!isMounted) return;

        setAuthorContext(nextAuthor);

        if (!nextCommunity || !nextHabit) {
          setPromptContextError(
            "Couldn't load the full Community and Habit context for prompt generation."
          );
        }
      } catch (error) {
        console.error("Error loading action form AI context", error);
        if (!isMounted) return;
        setPromptContextError(
          "Couldn't load the Community, Author, or Habit context for prompt generation."
        );
      } finally {
        if (isMounted) {
          setIsLoadingPromptContext(false);
        }
      }
    };

    fetchActionFormContext();

    return () => {
      isMounted = false;
    };
  }, [communityId, habitId]);

  const initialValues: Action = item || {
    id: generateRandomId(),
    name: "",
    description: "",
    habitId,
    //  tags: [],
    backgroundImageURL: "",
    duration: 0,
    goal: "",
    actionType: ActionType.CUSTOM,
    subactions: [],
    tags: [],
    createdAt: new Date().toISOString(),
    linkedActionPlanIds: [], // Add this line
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    description: Yup.string().required("Description is required"),
  });

  const handleSubmit = (values: Action, { setSubmitting }) => {
    if (mode === "create") {
      onCreateItem({ ...values, habitId });
    } else {
      onEditItem({ ...values, habitId });
    }
    setSubmitting(false);
    // onToggleFullScreenDialog();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        submitForm,
        isSubmitting,
        setFieldValue,
      }) => (
        <Form className={classes.form}>
          {promptContextError ? (
            <Box mb={2}>
              <Typography variant="body2" color="error">
                {promptContextError}
              </Typography>
            </Box>
          ) : null}
          <ActionFormButton
            onClick={submitForm}
            disabled={isSubmitting}
            entity="Action"
            canBeRemoved
            mode={mode}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Field name="name" as={TextField} label="Name" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field name="actionType">
                {({ field }) => (
                  <TextField {...field} select label="Action Type" fullWidth>
                    {actionTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {formatLabel(type)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field
                name="description"
                as={TextField}
                label="Description"
                fullWidth
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                name="duration"
                as={TextField}
                label="Duration (minutes)"
                type="number"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field name="goal" as={TextField} label="Goal" fullWidth />
            </Grid>
            <Grid item xs={12}>
              <Field
                name="tags"
                as={TextField}
                label="Tags (comma-separated)"
                fullWidth
                multiline
                rows={6}
                onChange={(e) => {
                  const tags = e.target.value
                    .split(",")
                    .map((tag) => tag.trim());
                  setFieldValue("tags", tags);
                }}
              />
              <Box mt={1}>
                {values?.tags &&
                  values?.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => {
                        const newTags = values.tags.filter(
                          (_, i) => i !== index
                        );
                        setFieldValue("tags", newTags);
                      }}
                      className={classes.chip}
                    />
                  ))}
              </Box>
              <Field
                name="position"
                as={TextField}
                label="Position"
                type="number"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth className={classes.learningPlanSelect}>
                <InputLabel id="learning-plans-label">
                  Related Learning Plans
                </InputLabel>
                <Select
                  labelId="learning-plans-label"
                  multiple
                  value={values.linkedActionPlanIds || []}
                  onChange={(event) => {
                    setFieldValue("linkedActionPlanIds", event.target.value);
                  }}
                  renderValue={(selected) => (
                    <Box display="flex" flexWrap="wrap">
                      {(selected as string[]).map((value) => {
                        const plan: Plan = learningPlans.find(
                          (p) => p.id === value
                        );
                        return (
                          <Chip
                            key={value}
                            label={plan ? plan.title : value}
                            className={classes.chip}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {learningPlans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      <Checkbox
                        checked={
                          values.linkedActionPlanIds?.indexOf(plan.id) > -1
                        }
                      />
                      <ListItemText primary={plan.title} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FieldArray name="subactions">
                {({ push, remove }) => (
                  <Paper className={classes.subactionsWorkspace} elevation={0}>
                    {/*
                      Reflection prompt generation uses saved Community/Habit context
                      and the current unsaved Action/Subaction values from this form.
                    */}
                    <Box className={classes.subactionsToolbar}>
                      <Box className={classes.subactionsToolbarContent}>
                        <Box display="flex" alignItems="center" flexWrap="wrap">
                          <Typography variant="h5" className={classes.subactionTitle}>
                            Subactions
                          </Typography>
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={`${values.subactions?.length || 0} total`}
                            className={classes.subactionCountChip}
                          />
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          Collapse cards to scan the list quickly. Expand one to edit details,
                          media, and metadata without losing context.
                        </Typography>
                      </Box>
                      <Box className={classes.subactionsToolbarActions}>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<AddIcon />}
                          onClick={() => push(createEmptySubaction())}
                        >
                          Add Subaction
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<GetAppIcon />}
                          disabled={!values.subactions || values.subactions.length === 0}
                          onClick={() => {
                            const fileName = `${values.name || "action"}-subactions.json`;
                            const blob = new Blob(
                              [JSON.stringify(values.subactions, null, 2)],
                              { type: "application/json" }
                            );
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = fileName;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                        >
                          Export Subactions
                        </Button>
                      </Box>
                    </Box>
                    <Divider />
                    <Box mt={3}>
                      {values.subactions && values.subactions.length > 0 ? (
                        values.subactions.map((subaction, index) => {
                          const generationKey = getSubactionGenerationKey(
                            subaction,
                            index
                          );

                          return (
                            <SubactionForm
                              key={subaction.id || index}
                              index={index}
                              remove={remove}
                              subaction={subaction}
                              setFieldValue={setFieldValue}
                              isPromptContextLoading={isLoadingPromptContext}
                              isGeneratingReflectionPrompt={
                                !!promptGenerationByKey[generationKey]
                              }
                              reflectionPromptError={
                                promptErrorByKey[generationKey] || ""
                              }
                              onGenerateReflectionPrompt={async (
                                subactionIndex
                              ) => {
                                const currentSubaction =
                                  values.subactions?.[subactionIndex];
                                if (!currentSubaction) return;

                                const currentGenerationKey =
                                  getSubactionGenerationKey(
                                    currentSubaction,
                                    subactionIndex
                                  );

                                if (isLoadingPromptContext) {
                                  setPromptErrorByKey((prev) => ({
                                    ...prev,
                                    [currentGenerationKey]:
                                      "Still loading the Community, Author, and Habit context.",
                                  }));
                                  return;
                                }

                                if (!communityContext || !habitContext) {
                                  setPromptErrorByKey((prev) => ({
                                    ...prev,
                                    [currentGenerationKey]:
                                      promptContextError ||
                                      "Missing Community or Habit context for prompt generation.",
                                  }));
                                  return;
                                }

                                setPromptGenerationByKey((prev) => ({
                                  ...prev,
                                  [currentGenerationKey]: true,
                                }));
                                setPromptErrorByKey((prev) => ({
                                  ...prev,
                                  [currentGenerationKey]: "",
                                }));

                                try {
                                  const generatedPrompt =
                                    await generateReflectionPromptPack(
                                      buildPromptPackContext({
                                        community: communityContext,
                                        author: authorContext,
                                        habit: habitContext,
                                        action: values,
                                        subaction: currentSubaction,
                                      })
                                    );

                                  setFieldValue(
                                    `subactions.${subactionIndex}.reflectionPrompt`,
                                    generatedPrompt
                                  );
                                } catch (error: any) {
                                  setPromptErrorByKey((prev) => ({
                                    ...prev,
                                    [currentGenerationKey]:
                                      error?.message ||
                                      "Couldn't generate the AI assistant prompt.",
                                  }));
                                } finally {
                                  setPromptGenerationByKey((prev) => ({
                                    ...prev,
                                    [currentGenerationKey]: false,
                                  }));
                                }
                              }}
                              isEnhancingPrompts={
                                !!enhanceGenerationByKey[generationKey]
                              }
                              enhanceError={
                                enhanceErrorByKey[generationKey] || ""
                              }
                              onEnhancePrompts={async (subactionIndex) => {
                                const currentSubaction =
                                  values.subactions?.[subactionIndex];
                                if (!currentSubaction) return;

                                const currentGenerationKey =
                                  getSubactionGenerationKey(
                                    currentSubaction,
                                    subactionIndex
                                  );

                                if (isLoadingPromptContext) {
                                  setEnhanceErrorByKey((prev) => ({
                                    ...prev,
                                    [currentGenerationKey]:
                                      "Still loading the Community, Author, and Habit context.",
                                  }));
                                  return;
                                }

                                if (!communityContext || !habitContext) {
                                  setEnhanceErrorByKey((prev) => ({
                                    ...prev,
                                    [currentGenerationKey]:
                                      promptContextError ||
                                      "Missing Community or Habit context for prompt generation.",
                                  }));
                                  return;
                                }

                                setEnhanceGenerationByKey((prev) => ({
                                  ...prev,
                                  [currentGenerationKey]: true,
                                }));
                                setEnhanceErrorByKey((prev) => ({
                                  ...prev,
                                  [currentGenerationKey]: "",
                                }));

                                const packContext = buildPromptPackContext({
                                  community: communityContext,
                                  author: authorContext,
                                  habit: habitContext,
                                  action: values,
                                  subaction: currentSubaction,
                                });

                                try {
                                  // Generate the chat prompt and the conversation
                                  // starters together so one click fills all of
                                  // the prompt subaction's AI-authored fields.
                                  const [generatedPrompt, starters] =
                                    await Promise.all([
                                      generateReflectionPromptPack(packContext),
                                      generatePromptStarters(packContext),
                                    ]);

                                  setFieldValue(
                                    `subactions.${subactionIndex}.reflectionPrompt`,
                                    generatedPrompt
                                  );
                                  setFieldValue(
                                    `subactions.${subactionIndex}.introMessages`,
                                    starters.introMessages
                                  );
                                  setFieldValue(
                                    `subactions.${subactionIndex}.suggestedReplies`,
                                    starters.suggestedReplies
                                  );
                                } catch (error: any) {
                                  setEnhanceErrorByKey((prev) => ({
                                    ...prev,
                                    [currentGenerationKey]:
                                      error?.message ||
                                      "Couldn't enhance the prompts.",
                                  }));
                                } finally {
                                  setEnhanceGenerationByKey((prev) => ({
                                    ...prev,
                                    [currentGenerationKey]: false,
                                  }));
                                }
                              }}
                            />
                          );
                        })
                      ) : (
                        <Box className={classes.emptyState}>
                          <Typography variant="subtitle1">
                            No subactions added yet
                          </Typography>
                          <Box mt={1}>
                            <Typography variant="body2" color="textSecondary">
                              Add the first subaction to break this action into clearer, trackable
                              steps.
                            </Typography>
                          </Box>
                          <Box mt={2}>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={() => push(createEmptySubaction())}
                            >
                              Add First Subaction
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                )}
              </FieldArray>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between">
                <Button onClick={onToggleFullScreenDialog} color="secondary">
                  Cancel
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Field name="backgroundImageURL">
                {({ field }) => (
                  <ImageUpload
                    field={field}
                    form={{ setFieldValue }}
                    actionId={item?.id}
                    reference={"actions"}
                  />
                )}
              </Field>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default ActionForm;
